// Status bar at the bottom: a fixed grid of labelled readouts, the logo at the right end.
import { el } from './dom.js';

export interface Cell {
  /** Small label above the value; empty: the cell holds only `content` (e.g. a button). */
  label: string;
  /** Grid column width, e.g. `120px` or `minmax(200px, 1fr)`. */
  width: string;
  /** Fixed content before the text, e.g. key caps. */
  content?: (Node | string)[];
  /** Hidden on narrow screens. */
  wide?: boolean;
}

/** `tail`: more pieces of text after it, each in its own colour (e.g. player names; '' = default). */
export type SetText = (text: string, color?: string, tail?: [string, string][]) => void;

const LOGO_WIDTH = '130px';

/** Frame-rate readout: the cell before the round's one (Wunsch User 2026-10-19); hidden on narrow screens. */
export const FPS_CELL: Cell = { label: 'FPS', width: '52px', wide: true };
/** The frame rate is shown over this long, so the number holds still enough to read. */
const FPS_WINDOW_MS = 500;

/** Counts the frames: call once per drawn frame; shows frames per second in `set`. */
export function fpsMeter(set: SetText): (now: number) => void {
  let [start, frames] = [performance.now(), 0];
  set('–');
  return (now) => {
    frames++;
    if (now - start < FPS_WINDOW_MS) return;
    set(String(Math.round((frames * 1000) / (now - start))));
    [start, frames] = [now, 0];
  };
}

/** Fills the bar with `cells`; returns a text setter per cell (touches the DOM only on change). */
export function statusBar(bar: HTMLElement, cells: Cell[]): SetText[] {
  // Narrow screens hide the wide cells, and their columns must go with them (index.html).
  const columns = (all: boolean) => [...cells.filter((c) => all || !c.wide).map((c) => c.width), LOGO_WIDTH].join(' ');
  bar.style.setProperty('--columns', columns(true));
  bar.style.setProperty('--columns-narrow', columns(false));
  const setters: SetText[] = [];
  const children = cells.map((c) => {
    const text = el('span');
    const after = el('span');
    let shown = '';
    setters.push((t, color = '', tail = []) => {
      if (text.textContent !== t) text.textContent = t;
      if (text.style.color !== color) text.style.color = color;
      const key = JSON.stringify(tail);
      if (key === shown) return;
      shown = key;
      after.replaceChildren(
        ...tail.map(([tt, tc]) => {
          const piece = el('span', { textContent: tt });
          piece.style.color = tc;
          return piece;
        }),
      );
    });
    const className = `cell${c.label ? '' : ' action'}${c.wide ? ' wide' : ''}`;
    if (!c.label) return el('div', { className }, ...(c.content ?? []));
    return el(
      'div',
      { className },
      el('span', { className: 'k', textContent: c.label }),
      el('span', { className: 'v' }, ...(c.content ?? []), text, after),
    );
  });
  bar.replaceChildren(...children, el('div', { className: 'cell logo', textContent: 'webTanks' }));
  bar.hidden = false;
  return setters;
}

/** Key caps, e.g. `keys('W', 'A', 'S', 'D')`. */
export function keys(...names: string[]): HTMLElement[] {
  return names.map((n) => el('kbd', { textContent: n }));
}
